import { Select } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TIME_FILTER } from 'constants/chart';
import type { TimeFilter } from 'types/chart';
import { getMonthName } from 'utils/date';
import {
  FILTER_BUTTONS,
  DATE_CONFIG,
  SELECT_CONFIG,
  SELECT_FIELDS,
  type FilterButtonsProps,
} from './types';

const FilterButtonsComponent = ({
  timeFilter,
  selectedWeek,
  selectedMonth,
  onTimeFilterChange,
  onSelectedWeekChange,
  onSelectedMonthChange,
}: FilterButtonsProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);

  const { monthOptions, yearOptions } = useMemo(() => {
    const currentYear = new Date().getFullYear();

    return {
      monthOptions: Array.from({ length: DATE_CONFIG.MONTHS_COUNT }, (_, i) => ({
        value: (i + 1).toString(),
        label: getMonthName(i + 1),
      })),
      yearOptions: Array.from({ length: DATE_CONFIG.YEARS_RANGE }, (_, i) => ({
        value: (currentYear - DATE_CONFIG.YEARS_OFFSET + i).toString(),
        label: (currentYear - DATE_CONFIG.YEARS_OFFSET + i).toString(),
      })),
    };
  }, []);

  const handleTimeFilterClick = (key: TimeFilter): void => {
    onTimeFilterChange(key);
    onSelectedWeekChange(null);
    if (key === TIME_FILTER.MONTH && !selectedMonth) {
      const now = new Date();
      onSelectedMonthChange({ year: now.getFullYear(), month: now.getMonth() + 1 });
    }
  };

  const handleDateChange = (
    value: string,
    field: 'month' | 'year',
    selectedMonth: { year: number; month: number } | null,
    onSelectedMonthChange: (month: { year: number; month: number } | null) => void
  ): void => {
    const newValue = parseInt(value);
    const now = new Date();

    if (selectedMonth) {
      onSelectedMonthChange({ ...selectedMonth, [field]: newValue });
    } else {
      const defaultValues = {
        month: newValue,
        year: field === 'year' ? newValue : now.getFullYear(),
      };
      onSelectedMonthChange({
        year: defaultValues.year,
        month: defaultValues.month,
      });
    }
  };

  const renderSelect = (
    field: keyof typeof SELECT_FIELDS,
    options: Array<{ value: string; label: string }>
  ): React.ReactElement => {
    const config = SELECT_FIELDS[field];
    return (
      <Select
        value={config.getValue(selectedMonth)}
        onValueChange={value =>
          handleDateChange(value, config.field, selectedMonth, onSelectedMonthChange)
        }
        options={options}
        placeholder={t(config.placeholderKey)}
      />
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2 justify-center sm:justify-end">
        {FILTER_BUTTONS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleTimeFilterClick(key as TimeFilter)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              timeFilter === key
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t(label)}
          </button>
        ))}
      </div>
      {timeFilter === TIME_FILTER.MONTH && !selectedWeek && (
        <div className="flex justify-center sm:justify-end">
          <div className={`flex ${SELECT_CONFIG.GAP}`}>
            {renderSelect('MONTH', monthOptions)}
            {renderSelect('YEAR', yearOptions)}
          </div>
        </div>
      )}
      {selectedWeek && (
        <div className="flex justify-center sm:justify-end">
          <button
            onClick={() => onSelectedWeekChange(null)}
            className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors"
          >
            {t('DailyTracking.Chart.backToWeeks')}
          </button>
        </div>
      )}
    </div>
  );
};

export const FilterButtons = memo(FilterButtonsComponent);
